import { useState, useEffect, useRef } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  Sidebar,
  Header,
  focusPageAfterSidebarClose,
  Breadcrumbs,
  CRMPipelineKeepAlive,
  CompanyPreferencesGate,
  SubscriptionExpired,
} from '@/modules/dashboard';
import { apiClient } from '@/common/lib/api-client';
import { PermissionProvider } from '@/common/providers/PermissionProvider';
import { FeaturesProvider } from '@/common/providers/FeaturesProvider';
import {
  ServerError,
  AccessDenied,
  NotificationPermissionModal,
} from '@/common/components';
import { useOneSignal } from '@/common/hooks/useOneSignal';
import { ONBOARDING_ROUTES } from '../onboardingRoutes';
import { useBodyScrollLock, forceResetBodyScrollLock } from '@/common/hooks';

const routeFeatures = {
  '/crm-pipeline/settings': 'configuration',
  '/dashboard': 'dashboard',
  '/contacts': 'contacts',
  '/templates': 'templates',
  '/campaigns': 'campaign',
  '/whatsapp-chat': 'inbox',
  '/email-inbox': 'inbox',
  '/employees': 'users',
  '/whatsapp-flows': 'flows',
  '/crm-dashboard': 'crm',
  '/crm-dashboard/task': 'crm',
  '/crm-dashboard/followup': 'crm',
  '/crm-contacts': 'leads',
  '/crm-contacts/overview': 'leads',
  '/crm-pipeline': 'inquires',
  '/crm-segments': 'groups',
  '/reminders': 'reminders',
  '/calendar': 'appointments',
  '/crm-settings': 'configuration',
  '/integrations': 'integrations',
  '/quotations': 'quotations',
};

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const sidebarOpenRef = useRef(false);
  sidebarOpenRef.current = sidebarOpen;
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState(null);
  const [features, setFeatures] = useState(null);
  const [subscription, setSubscription] = useState(() => {
    try {
      const stored = localStorage.getItem('subscription');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [errorState, setErrorState] = useState(null);
  const { consumePendingRoute } = useOneSignal();

  useBodyScrollLock(sidebarOpen);

  const token = localStorage.getItem('access_token');
  const location = useLocation();

  useEffect(() => {
    if (!token || loading) return;
    consumePendingRoute();
  }, [token, loading, consumePendingRoute]);

  const fetchUserDataAndFeatures = async () => {
    try {
      const response = await apiClient.get('/v1/accounts/me/');
      const { user, subscription } = response.data;
      setUserStatus(user);

      // Sync local storage state
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('company_id', user.company_id);
      localStorage.setItem('company_name', user.company_name);

      setSubscription(subscription || null);
      if (subscription) {
        localStorage.setItem('subscription', JSON.stringify(subscription));
      } else {
        localStorage.removeItem('subscription');
      }

      if (subscription && subscription.features) {
        let parsedFeatures = null;
        try {
          if (typeof subscription.features === 'string') {
            // Base64 decode
            const decoded = atob(subscription.features);
            const parsedArray = JSON.parse(decoded);
            parsedFeatures = parsedArray[0] || null;
          } else if (Array.isArray(subscription.features)) {
            parsedFeatures = subscription.features[0] || null;
          }
        } catch (err) {
          console.error('Failed to parse subscription features:', err);
        }
        // console.log('Decrypted subscription features:', parsedFeatures);
        setFeatures(parsedFeatures);
        return parsedFeatures;
      } else {
        setFeatures(null);
        return null;
      }
    } catch (error) {
      console.error('Error fetching user data and features:', error);
      throw error;
    }
  };

  const refreshFeatures = async () => {
    try {
      await fetchUserDataAndFeatures();
    } catch (err) {
      console.error('Failed to refresh features in background:', err);
    }
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    const checkOnboardingStatus = async () => {
      try {
        await fetchUserDataAndFeatures();
      } catch (error) {
        console.error('Error checking onboarding status:', error);
        setErrorState(error);
      } finally {
        setLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [token]);

  // Route-based authorization check (Synchronous to prevent flicker)
  // Only run this check when the user has COMPLETED onboarding — otherwise
  // the feature authorization toast fires erroneously during mid-onboarding redirects.
  // Also skip entirely when subscription is expired — the SubscriptionExpired guard
  // below takes over and no feature-redirect or toast should fire.
  const onboardingComplete =
    userStatus && userStatus.onboarding_step === 'COMPLETED';
  const subscriptionExpired =
    subscription !== null && subscription?.is_active === false;
  let redirectTarget = null;
  if (onboardingComplete && features !== null && !subscriptionExpired) {
    const currentPath = location.pathname;
    const matchingKey = Object.keys(routeFeatures).find(
      (route) => currentPath === route || currentPath.startsWith(route + '/'),
    );

    if (matchingKey) {
      const requiredFeature = routeFeatures[matchingKey];
      if (!features[requiredFeature]) {
        console.warn(
          `Access denied for ${currentPath}. Feature '${requiredFeature}' is disabled.`,
        );

        if (features['dashboard']) {
          redirectTarget = '/dashboard';
        } else {
          const firstAllowed = Object.keys(routeFeatures).find(
            (route) => features[routeFeatures[route]],
          );
          redirectTarget = firstAllowed || '/settings/profile';
        }
      }
    }
  }

  useEffect(() => {
    // Clear CRM Contacts filters when navigating outside of the CRM contacts flow
    if (!location.pathname.startsWith('/crm-contacts')) {
      sessionStorage.removeItem('crm-contacts-filters');
    }
    // Keep calendar snapshot while on calendar or lead overview (return path)
    if (
      !location.pathname.startsWith('/calendar') &&
      location.pathname !== '/crm-contacts/overview'
    ) {
      sessionStorage.removeItem('calendar-snapshot');
    }
    // Keep quotations snapshot while within quotations module
    if (!location.pathname.startsWith('/quotations')) {
      sessionStorage.removeItem('quotations-state-snapshot');
      sessionStorage.removeItem('quotation-customer-name');
    }
    if (location.pathname !== '/not-found') {
      sessionStorage.setItem('aexon-return-path', location.pathname);
    }
    // Clean up any stale scroll locks and close mobile sidebar on navigation
    forceResetBodyScrollLock();
    const drawerWasOpen = sidebarOpenRef.current;
    if (drawerWasOpen && window.matchMedia('(max-width: 1023px)').matches) {
      focusPageAfterSidebarClose();
    }
    setSidebarOpen(false);
  }, [location.pathname]);

  // Guard 1: If not logged in, redirect to login page
  if (!token) {
    return <Navigate to="/login" replace />;
  }

  // Guard 2: Loading State (Render a premium glassmorphic spinner)
  if (loading) {
    return (
      <div className="h-screen w-full bg-layer-1-page flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-t-primary border-r-transparent border-b-primary border-l-transparent animate-spin" />
          <p className="text-text-secondary text-sm tracking-wide">
            Validating session status...
          </p>
        </div>
      </div>
    );
  }

  // Guard 2.5: Error State
  if (errorState) {
    if (!errorState.response) {
      return <ServerError onRetry={() => window.location.reload()} />;
    }
    if (errorState.response.status >= 500) {
      return <ServerError onRetry={() => window.location.reload()} />;
    }
    if (errorState.response.status === 403) {
      return <AccessDenied />;
    }
    if (errorState.response.status === 401) {
      // Typically handled by Axios interceptor, but fallback to login
      return <Navigate to="/login" replace />;
    }
    return <ServerError onRetry={() => window.location.reload()} />;
  }

  // Guard 3: Onboarding step redirect if not COMPLETED (runs BEFORE feature check)
  if (userStatus && userStatus.onboarding_step !== 'COMPLETED') {
    const targetRoute =
      ONBOARDING_ROUTES[userStatus.onboarding_step] || '/company-registration';
    setTimeout(() => {
      toast.error(
        'Please complete your registration process before accessing the dashboard.',
        { id: 'onboarding-incomplete' },
      );
    }, 0);
    return <Navigate to={targetRoute} replace />;
  }

  // Guard 4: Feature Authorization Check Redirect (only for fully onboarded users)
  if (redirectTarget) {
    // We defer the toast slightly to allow Navigate to do its job without breaking React render phase side effects
    setTimeout(() => {
      toast.error(
        "You don't have access to this feature. Please upgrade your subscription.",
      );
    }, 0);
    return <Navigate to={redirectTarget} replace />;
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="h-screen h-[100dvh] max-h-[100dvh] w-full bg-surface-mint text-text-primary font-sf-pro-display flex overflow-hidden">
      <Toaster position="top-center" reverseOrder={false} />

      {/* Sidebar */}
      <FeaturesProvider features={features} subscription={subscription}>
        <PermissionProvider
          permissions={userStatus?.permissions || []}
          role={userStatus?.role || null}
        >
          <CompanyPreferencesGate companyId={userStatus?.company_id} />
          <Sidebar
            isOpen={sidebarOpen}
            toggleSidebar={toggleSidebar}
            features={features}
            refreshFeatures={refreshFeatures}
          />

          {/* Mobile Sidebar overlay — mousedown avoids ghost click-through from closing modals */}
          {sidebarOpen && (
            <div
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) {
                  setSidebarOpen(false);
                }
              }}
              className="fixed inset-0 z-40 bg-overlay-backdrop backdrop-blur-sm lg:hidden"
            />
          )}

          {/* Main Content Column */}
          <div
            id="dashboard-content"
            className="flex flex-col flex-1 h-full min-w-0 overflow-hidden gap-3 p-3 lg:p-4 laptop:!gap-2 laptop:!p-2.5"
          >
            {/* Floating Header Card */}
            <Header toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} />

            {/* Breadcrumbs on Mint Background */}
            <div className="shrink-0 px-1">
              <Breadcrumbs />
            </div>

            {/* Scrollable Dashboard Grid Content — overflow clipped so full-bleed
                tools (Flow Builder, Chat) cannot stretch the page. Pages that
                need scroll own it internally (e.g. DashboardPage). */}
            <main className="flex-1 w-full min-h-0 relative flex flex-col overflow-hidden">
              <div className="w-full h-full min-h-0 max-h-full overflow-hidden flex flex-col flex-1 relative">
                <CRMPipelineKeepAlive>
                  {/* When subscription is expired, always show the expired screen
                      inside the shell — sidebar and header remain visible */}
                  {subscriptionExpired ? (
                    <SubscriptionExpired />
                  ) : (
                    <Outlet context={{ subscription, userStatus }} />
                  )}
                </CRMPipelineKeepAlive>
              </div>
            </main>
          </div>
        </PermissionProvider>
      </FeaturesProvider>
      <NotificationPermissionModal />
    </div>
  );
}
